import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, message, Modal, Radio, Alert } from 'antd';
import api from '@services/api';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';
import { IContract } from '../types';

import './styles.less';

interface EnableDisableProps {
  selectedContracts: IContract[];
  isOpen: boolean;
  onClose?: Function;
  onFinish?: Function;
}

const EnableDisable = ({
  selectedContracts,
  isOpen,
  onFinish,
  onClose,
}: EnableDisableProps) => {
  const { t } = useTranslation(['customer-contracts']);
  const [isEnable, setIsEnable] = useState();
  const [putIsLoading, setPutIsLoading] = useState(false);

  const assetNumberList = useMemo(
    () => selectedContracts?.map(contract => contract.assetNumber).join(','),
    [selectedContracts],
  );

  const putFlag = useCallback(
    () =>
      api.put('service-proxy/contracts/customer/updateFlagVisible', undefined, {
        params: {
          assetNumberList,
          flagVisible: isEnable,
        },
      }),
    [assetNumberList, isEnable],
  );

  const handleFinish = useCallback(() => {
    setIsEnable(undefined);
    onFinish?.();
    onClose?.();
  }, [onClose, onFinish]);

  const handleConfirm = useCallback(async () => {
    setPutIsLoading(true);
    try {
      await putFlag();
      message.success(t('modal-actions.submit-success'));
    } catch {
      message.error(t('modal-actions.submit-error'));
    }
    handleFinish();
    setPutIsLoading(false);
  }, [handleFinish, putFlag, t]);

  return (
    <Modal
      title={t('contracts-menu.enable-disable')}
      open={isOpen}
      onCancel={() => onClose?.()}
      footer={
        <div className="contract-enable-disable-footer">
          <span>
            {t('selected-contracts', {
              count: selectedContracts?.length,
            })}
          </span>
          <Button
            type="primary"
            onClick={handleConfirm}
            disabled={putIsLoading || !isEnable}
            loading={putIsLoading}
          >
            {t('modal-actions.confirm')}
          </Button>
        </div>
      }
    >
      <Radio.Group
        value={isEnable}
        onChange={({ target: { value } }) => setIsEnable(value)}
        className="contract-enable-disable-radio"
      >
        <Radio.Button value="true">
          <AiOutlineEye />
          <span>{t('enable')}</span>
        </Radio.Button>
        <Radio.Button value="false">
          <AiOutlineEyeInvisible />
          <span>{t('disable')}</span>
        </Radio.Button>
      </Radio.Group>
      {isEnable && (
        <Alert
          showIcon
          type={isEnable === 'true' ? 'success' : 'error'}
          message={t('selected-contracts-help', {
            count: selectedContracts?.length,
            type: t(isEnable === 'true' ? 'enabled2' : 'disabled2', {
              count: selectedContracts?.length,
            })?.toLowerCase(),
          })}
          className="contract-enable-disable-alert"
        />
      )}
    </Modal>
  );
};

export default EnableDisable;
